<script lang="ts">
    import { trans } from '$assets/translator';
    import Button from '$saga/components/Button.svelte';

    export let userEmail: string;
    export let submitUrl: string;

    const MAX_FILES = 5;
    const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

    const INTERFACES = [
        {
            value: 'espace_client',
            label: trans('backpack.bug_report.interface.espace_client'),
        },
        {
            value: 'site_decathlon_travel',
            label: trans('backpack.bug_report.interface.site_decathlon_travel'),
        },
        {
            value: 'display',
            label: trans('backpack.bug_report.interface.display'),
        },
        {
            value: 'partner',
            label: trans('backpack.bug_report.interface.partner'),
        },
        {
            value: 'backpack',
            label: trans('backpack.bug_report.interface.backpack'),
        },
        {
            value: 'saga',
            label: trans('backpack.bug_report.interface.saga'),
        },
        {
            value: 'hubspot',
            label: trans('backpack.bug_report.interface.hubspot'),
        },
        {
            value: 'emails',
            label: trans('backpack.bug_report.interface.emails'),
        },
    ] as const;

    const BROWSERS = [
        { value: 'chrome', label: trans('backpack.bug_report.browser.chrome') },
        {
            value: 'firefox',
            label: trans('backpack.bug_report.browser.firefox'),
        },
        { value: 'safari', label: trans('backpack.bug_report.browser.safari') },
    ] as const;

    let selectedInterface: string = '';
    let title: string = '';
    let currentBehavior: string = '';
    let expectedBehavior: string = '';
    let stepsToReproduce: string = '';
    let browser: string = '';
    let attachments: File[] = [];
    let fileError: string | null = null;

    let isSubmitting = false;
    let submitted = false;
    let submitError: string | null = null;

    let fileInput: HTMLInputElement;

    function handleFiles(event: Event) {
        const input = event.target as HTMLInputElement;
        const newFiles = Array.from(input.files ?? []);
        fileError = null;

        const invalid = newFiles.filter(
            (f) => !ACCEPTED_TYPES.includes(f.type),
        );
        if (invalid.length > 0) {
            fileError = trans(
                'backpack.bug_report.form.attachments_error_type',
            );
            input.value = '';
            return;
        }

        const combined = [...attachments, ...newFiles];
        if (combined.length > MAX_FILES) {
            fileError = trans(
                'backpack.bug_report.form.attachments_error_max',
                { '%max%': String(MAX_FILES) },
            );
            input.value = '';
            return;
        }

        attachments = combined;
        input.value = '';
    }

    function removeFile(index: number) {
        attachments = attachments.filter((_, i) => i !== index);
    }

    async function handleSubmit() {
        isSubmitting = true;
        submitError = null;

        const formData = new FormData();
        formData.append('interface', selectedInterface);
        formData.append('title', title);
        formData.append('currentBehavior', currentBehavior);
        formData.append('expectedBehavior', expectedBehavior);
        formData.append('stepsToReproduce', stepsToReproduce);
        formData.append('browser', browser);
        attachments.forEach((file) => formData.append('attachments[]', file));

        try {
            const response = await fetch(submitUrl, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('server_error');
            }

            submitted = true;
        } catch {
            submitError = trans('backpack.bug_report.form.error');
        } finally {
            isSubmitting = false;
        }
    }

    $: isValid =
        selectedInterface !== '' &&
        title.trim() !== '' &&
        currentBehavior.trim() !== '' &&
        expectedBehavior.trim() !== '' &&
        stepsToReproduce.trim() !== '' &&
        browser !== '';
</script>

<div class="w-full max-w-[860px] bg-white p-4xl">
    {#if submitted}
        <div class="flex flex-col items-center gap-l py-4xl text-center">
            <svg
                class="size-[56px] text-status-positive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
            <h2 class="text-2xl font-semibold text-content-neutral">
                {trans('backpack.bug_report.form.success_title')}
            </h2>
            <p class="max-w-sm text-base text-content-quiet">
                {trans('backpack.bug_report.form.success_message')}
            </p>
        </div>
    {:else}
        <h1 class="mb-3xl text-2xl font-semibold text-content-neutral">
            {trans('backpack.bug_report.form.title')}
        </h1>

        <form
            on:submit|preventDefault={handleSubmit}
            class="flex flex-col gap-2xl"
            novalidate
        >
            <div class="grid grid-cols-2 gap-2xl">
                <!-- Email (read-only) -->
                <div class="flex flex-col gap-xs">
                    <label
                        for="email"
                        class="text-sm font-medium text-content-neutral"
                    >
                        {trans('backpack.bug_report.form.email_label')}
                    </label>
                    <input
                        name="email"
                        type="email"
                        value={userEmail}
                        readonly
                        class="w-full cursor-not-allowed border border-solid border-bord-quiet bg-container-quiet px-m py-s text-sm text-content-quiet"
                    />
                </div>

                <!-- Interface -->
                <div class="flex flex-col gap-xs">
                    <label
                        for="bug-interface"
                        class="text-sm font-medium text-content-neutral"
                    >
                        {trans('backpack.bug_report.form.interface_label')}
                        <span class="ml-3xs text-status-negative"
                            >{trans('backpack.bug_report.form.required')}</span
                        >
                    </label>
                    <select
                        id="bug-interface"
                        bind:value={selectedInterface}
                        required
                        class="w-full border border-solid border-bord-quiet bg-white px-m py-s text-sm text-content-neutral"
                    >
                        <option value="" disabled
                            >{trans(
                                'backpack.bug_report.form.interface_placeholder',
                            )}</option
                        >
                        {#each INTERFACES as item (item.value)}
                            <option value={item.value}>{item.label}</option>
                        {/each}
                    </select>
                </div>
            </div>

            <!-- Bug title -->
            <div class="flex flex-col gap-xs">
                <label
                    for="bug-title"
                    class="text-sm font-medium text-content-neutral"
                >
                    {trans('backpack.bug_report.form.bug_title_label')}
                    <span class="ml-3xs text-status-negative"
                        >{trans('backpack.bug_report.form.required')}</span
                    >
                </label>
                <input
                    id="bug-title"
                    type="text"
                    bind:value={title}
                    maxlength="200"
                    required
                    placeholder={trans(
                        'backpack.bug_report.form.bug_title_placeholder',
                    )}
                    class="w-full border border-solid border-bord-quiet bg-white px-m py-s text-sm text-content-neutral placeholder:text-content-quiet"
                />
            </div>

            <div class="grid grid-cols-2 gap-2xl">
                <!-- Current behavior -->
                <div class="flex flex-col gap-xs">
                    <label
                        for="bug-current"
                        class="text-sm font-medium text-content-neutral"
                    >
                        {trans(
                            'backpack.bug_report.form.current_behavior_label',
                        )}
                        <span class="ml-3xs text-status-negative"
                            >{trans('backpack.bug_report.form.required')}</span
                        >
                    </label>
                    <textarea
                        id="bug-current"
                        bind:value={currentBehavior}
                        required
                        rows="5"
                        placeholder={trans(
                            'backpack.bug_report.form.current_behavior_placeholder',
                        )}
                        class="w-full resize-y border border-solid border-bord-quiet bg-white px-m py-s text-sm text-content-neutral placeholder:text-content-quiet"
                    ></textarea>
                </div>

                <!-- Expected behavior -->
                <div class="flex flex-col gap-xs">
                    <label
                        for="bug-expected"
                        class="text-sm font-medium text-content-neutral"
                    >
                        {trans(
                            'backpack.bug_report.form.expected_behavior_label',
                        )}
                        <span class="ml-3xs text-status-negative"
                            >{trans('backpack.bug_report.form.required')}</span
                        >
                    </label>
                    <textarea
                        id="bug-expected"
                        bind:value={expectedBehavior}
                        required
                        rows="5"
                        placeholder={trans(
                            'backpack.bug_report.form.expected_behavior_placeholder',
                        )}
                        class="w-full resize-y border border-solid border-bord-quiet bg-white px-m py-s text-sm text-content-neutral placeholder:text-content-quiet"
                    ></textarea>
                </div>
            </div>

            <!-- Steps to reproduce -->
            <div class="flex flex-col gap-xs">
                <label
                    for="bug-steps"
                    class="text-sm font-medium text-content-neutral"
                >
                    {trans('backpack.bug_report.form.steps_label')}
                    <span class="ml-3xs text-status-negative"
                        >{trans('backpack.bug_report.form.required')}</span
                    >
                </label>
                <textarea
                    id="bug-steps"
                    bind:value={stepsToReproduce}
                    required
                    rows="4"
                    placeholder={trans(
                        'backpack.bug_report.form.steps_placeholder',
                    )}
                    class="w-full resize-y border border-solid border-bord-quiet bg-white px-m py-s text-sm text-content-neutral placeholder:text-content-quiet"
                ></textarea>
            </div>

            <div class="grid grid-cols-2 items-start gap-2xl">
                <!-- Browser -->
                <div class="flex flex-col gap-xs">
                    <label
                        for="bug-browser"
                        class="text-sm font-medium text-content-neutral"
                    >
                        {trans('backpack.bug_report.form.browser_label')}
                        <span class="ml-3xs text-status-negative"
                            >{trans('backpack.bug_report.form.required')}</span
                        >
                    </label>
                    <select
                        id="bug-browser"
                        bind:value={browser}
                        required
                        class="w-full border border-solid border-bord-quiet bg-white px-m py-s text-sm text-content-neutral"
                    >
                        <option value="" disabled
                            >{trans(
                                'backpack.bug_report.form.browser_placeholder',
                            )}</option
                        >
                        {#each BROWSERS as item (item.value)}
                            <option value={item.value}>{item.label}</option>
                        {/each}
                    </select>
                </div>

                <!-- File upload -->
                <div class="flex flex-col gap-xs">
                    <label
                        for="attachments[]"
                        class="text-sm font-medium text-content-neutral"
                    >
                        {trans('backpack.bug_report.form.attachments_label')}
                        <span
                            class="ml-3xs text-sm font-normal text-content-quiet"
                        >
                            {trans(
                                'backpack.bug_report.form.attachments_hint',
                                { '%max%': String(MAX_FILES) },
                            )}
                        </span>
                    </label>

                    <Button
                        type="button"
                        intent="secondary"
                        size="medium"
                        on:click={() => fileInput.click()}
                        disabled={attachments.length >= MAX_FILES}
                        class="justify-start"
                    >
                        {trans('backpack.bug_report.form.attachments_add', {
                            '%current%': String(attachments.length),
                            '%max%': String(MAX_FILES),
                        })}
                    </Button>

                    <input
                        bind:this={fileInput}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        multiple
                        on:change={handleFiles}
                        class="hidden"
                        name="attachments[]"
                    />

                    {#if fileError}
                        <p class="text-xs text-status-negative">{fileError}</p>
                    {/if}

                    {#if attachments.length > 0}
                        <ul class="flex flex-col gap-xs">
                            {#each attachments as file, i (file)}
                                <li
                                    class="flex items-center justify-between gap-s border border-solid border-bord-quiet bg-container-quiet px-m py-xs text-sm"
                                >
                                    <span class="truncate text-content-neutral"
                                        >{file.name}</span
                                    >
                                    <button
                                        type="button"
                                        on:click={() => removeFile(i)}
                                        aria-label={trans(
                                            'backpack.bug_report.form.attachments_remove',
                                        )}
                                        class="shrink-0 text-content-quiet transition-colors hover:text-status-negative"
                                    >
                                        <svg
                                            class="size-m"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            stroke-width="2"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </li>
                            {/each}
                        </ul>
                    {/if}
                </div>
            </div>

            {#if submitError}
                <p class="text-sm text-status-negative">{submitError}</p>
            {/if}

            <div class="flex justify-end pt-xs">
                <Button
                    type="submit"
                    intent="primary"
                    size="large"
                    disabled={!isValid || isSubmitting}
                >
                    {isSubmitting
                        ? trans('backpack.bug_report.form.submitting')
                        : trans('backpack.bug_report.form.submit')}
                </Button>
            </div>
        </form>
    {/if}
</div>
